/**
 * Offline signed licence files (ORGU-404, ADR 0051):
 *
 * Self-hosted hubs accept an offline signed licence file holding the org, plan, entitlements,
 * and expiry with an Ed25519 signature. The hub verifies it against an embedded public key
 * without network access, unlocking paid plan features offline.
 *
 * Tampering with any byte of the payload fails signature verification with a 'signature invalid' error.
 * A licence whose expiry date has passed is refused with an 'expired' error.
 */
import type { PlanId } from './entitlements.ts';
import { isPlanId } from './entitlements.ts';
import { canonicalJson } from './trust.ts';
import type { Ed25519Verify } from './release.ts';

/** Default embedded Ed25519 public key (raw 32 bytes in base64). */
export const DEFAULT_LICENCE_ISSUER_PUBLIC_KEY = 'asd/VQ7TIHBu+RhZyaYKKBGlvJ9ri80ziTSNoNS8LXs=';

/** Default issuer test seed (raw 32 bytes in base64) for signing offline licences in tests and tools. */
export const DEFAULT_LICENCE_ISSUER_SEED = '90+wjx5YGL3r7Tbt7tS6uOImLr8Ip/U1ADhTzGsoYQw=';

const SPKI_ED25519_PREFIX = Uint8Array.from([0x30, 0x2a, 0x30, 0x05, 0x06, 0x03, 0x2b, 0x65, 0x70, 0x03, 0x21, 0x00]);
const PKCS8_ED25519_PREFIX = Uint8Array.from([0x30, 0x2e, 0x02, 0x01, 0x00, 0x30, 0x05, 0x06, 0x03, 0x2b, 0x65, 0x70, 0x04, 0x22, 0x04, 0x20]);

/** Payload inside an offline licence file. */
export interface LicencePayload {
  org: string;
  plan: PlanId;
  entitlements?:
    | {
        add?: string[];
        remove?: string[];
        limits?: Record<string, number | null>;
      }
    | string[];
  expiry: number | string;
}

/** Complete signed licence document as stored or transmitted. */
export interface SignedLicence {
  payload?: LicencePayload | Record<string, unknown>;
  signature: string;
  org?: string;
  plan?: PlanId;
  entitlements?:
    | {
        add?: string[];
        remove?: string[];
        limits?: Record<string, number | null>;
      }
    | string[];
  expiry?: number | string;
}

export interface ParsedLicence {
  org: string;
  plan: PlanId;
  overrides: {
    add?: string[];
    remove?: string[];
    limits?: Record<string, number | null>;
  };
  expiry: number;
  expiryRaw: number | string;
  signature: string;
}

export type LicenceVerificationResult =
  | { ok: true; licence: ParsedLicence }
  | { ok: false; error: 'signature invalid' | 'expired' | 'malformed'; code: string; message: string };

function getGlobalBuffer(): any {
  return (globalThis as any).Buffer;
}

function b64ToUint8(b64: string): Uint8Array {
  const Buf = getGlobalBuffer();
  if (Buf) {
    return Uint8Array.from(Buf.from(b64, 'base64'));
  }
  const atobFn = (globalThis as any).atob;
  if (typeof atobFn === 'function') {
    const bin = atobFn(b64);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    return arr;
  }
  throw new Error('No base64 decoder available');
}

function uint8ToB64(u: Uint8Array): string {
  const Buf = getGlobalBuffer();
  if (Buf) {
    return Buf.from(u).toString('base64');
  }
  const btoaFn = (globalThis as any).btoa;
  if (typeof btoaFn === 'function') {
    let s = '';
    for (let i = 0; i < u.length; i++) s += String.fromCharCode(u[i]!);
    return btoaFn(s);
  }
  throw new Error('No base64 encoder available');
}

function concat(a: Uint8Array, b: Uint8Array): Uint8Array {
  const c = new Uint8Array(a.length + b.length);
  c.set(a, 0);
  c.set(b, a.length);
  return c;
}

function getNodeCrypto(): any {
  try {
    const proc = (globalThis as any).process;
    if (typeof proc?.getBuiltinModule === 'function') {
      return proc.getBuiltinModule('node:crypto');
    }
    if (typeof proc?.mainModule?.require === 'function') {
      return proc.mainModule.require('node:crypto');
    }
  } catch {
    return null;
  }
  return null;
}

/** Node.js native Ed25519 verify helper. */
export function nodeVerifyEd25519(publicKeyBase64: string, message: string, signatureBase64: string): boolean {
  try {
    const raw = b64ToUint8(publicKeyBase64);
    const spki = raw.length === 32 ? concat(SPKI_ED25519_PREFIX, raw) : raw;
    const cryptoModule = getNodeCrypto();
    if (!cryptoModule) return false;
    const Buf = getGlobalBuffer();
    if (!Buf) return false;
    const key = cryptoModule.createPublicKey({ key: Buf.from(spki), format: 'der', type: 'spki' });
    return cryptoModule.verify(null, Buf.from(message, 'utf8'), key, Buf.from(signatureBase64, 'base64'));
  } catch {
    return false;
  }
}

/** Node.js native Ed25519 sign helper. */
export function nodeSignEd25519(seedBase64: string, message: string): string {
  const raw = b64ToUint8(seedBase64);
  const pkcs8 = raw.length === 32 ? concat(PKCS8_ED25519_PREFIX, raw) : raw;
  const cryptoModule = getNodeCrypto();
  if (!cryptoModule) throw new Error('node:crypto is not available for signing');
  const Buf = getGlobalBuffer();
  if (!Buf) throw new Error('Buffer is not available for signing');
  const key = cryptoModule.createPrivateKey({ key: Buf.from(pkcs8), format: 'der', type: 'pkcs8' });
  return cryptoModule.sign(null, Buf.from(message, 'utf8'), key).toString('base64');
}

/** Parse an expiry string or timestamp to milliseconds. */
export function parseLicenceExpiry(val: unknown): number | null {
  if (typeof val === 'number') {
    if (!Number.isFinite(val) || val <= 0) return null;
    return val < 1e11 ? val * 1000 : val;
  }
  if (typeof val === 'string') {
    const parsed = Date.parse(val);
    if (!Number.isFinite(parsed)) return null;
    return parsed;
  }
  return null;
}

function normalizeOverrides(entitlements: unknown): ParsedLicence['overrides'] {
  if (Array.isArray(entitlements)) {
    return { add: entitlements.filter((x): x is string => typeof x === 'string') };
  }
  if (entitlements && typeof entitlements === 'object') {
    const o = entitlements as Record<string, unknown>;
    const add = Array.isArray(o.add) ? o.add.filter((x): x is string => typeof x === 'string') : undefined;
    const remove = Array.isArray(o.remove) ? o.remove.filter((x): x is string => typeof x === 'string') : undefined;
    const limits = o.limits && typeof o.limits === 'object' ? (o.limits as Record<string, number | null>) : undefined;
    return { ...(add ? { add } : {}), ...(remove ? { remove } : {}), ...(limits ? { limits } : {}) };
  }
  return {};
}

/** Canonical signing inputs for a licence payload. */
export function licenceSigningCandidates(payload: unknown): string[] {
  if (typeof payload === 'string') {
    const list = [payload];
    try {
      const parsed = JSON.parse(payload);
      list.push(canonicalJson(parsed), `orgus-licence-v1\n${canonicalJson(parsed)}`);
    } catch {
      // not JSON
    }
    return list;
  }
  if (payload && typeof payload === 'object') {
    const canon = canonicalJson(payload);
    return [canon, `orgus-licence-v1\n${canon}`, JSON.stringify(payload)];
  }
  return [];
}

/**
 * Sign an offline licence payload with an Ed25519 issuer key.
 * By default uses DEFAULT_LICENCE_ISSUER_SEED.
 */
export function signLicence(
  payload: LicencePayload | Record<string, unknown>,
  opts: {
    seed?: string;
    signer?: (message: string) => string;
    format?: 'payload-wrapped' | 'flat';
  } = {},
): SignedLicence {
  const seed = opts.seed ?? DEFAULT_LICENCE_ISSUER_SEED;
  const signer = opts.signer ?? ((msg: string) => nodeSignEd25519(seed, msg));

  const normalized: LicencePayload = {
    org: typeof (payload as any).org === 'string' ? (payload as any).org : String((payload as any).orgId ?? 'self-hosted'),
    plan: (payload as any).plan,
    ...((payload as any).entitlements !== undefined ? { entitlements: (payload as any).entitlements } : {}),
    expiry: (payload as any).expiry ?? (payload as any).expires ?? (payload as any).validUntil,
  };

  const message = canonicalJson(normalized);
  const signature = signer(message);

  if (opts.format === 'flat') {
    return {
      org: normalized.org,
      plan: normalized.plan,
      entitlements: normalized.entitlements,
      expiry: normalized.expiry,
      signature,
    };
  }

  return {
    payload: normalized,
    signature,
  };
}

/**
 * Verifies a licence file: checks signature against the issuer public key and verifies expiry.
 */
export function verifyLicenceSync(
  input: unknown,
  opts: {
    publicKeyBase64?: string;
    now?: number;
    verifier?: (pub: string, msg: string, sig: string) => boolean;
  } = {},
): LicenceVerificationResult {
  if (!input) {
    return { ok: false, error: 'malformed', code: 'bad-request', message: 'Licence content is empty' };
  }

  let doc: Record<string, unknown>;

  if (typeof input === 'string') {
    try {
      doc = JSON.parse(input) as Record<string, unknown>;
    } catch {
      return { ok: false, error: 'malformed', code: 'bad-json', message: 'Licence is not valid JSON' };
    }
  } else if (typeof input === 'object' && input !== null) {
    doc = input as Record<string, unknown>;
  } else {
    return { ok: false, error: 'malformed', code: 'bad-request', message: 'Licence must be an object or JSON string' };
  }

  // Handle nested licence field e.g. { licence: ... }
  if (doc.licence && typeof doc.licence === 'object') {
    doc = doc.licence as Record<string, unknown>;
  } else if (typeof doc.licence === 'string') {
    try {
      doc = JSON.parse(doc.licence) as Record<string, unknown>;
    } catch {
      return { ok: false, error: 'malformed', code: 'bad-json', message: 'Licence string is not valid JSON' };
    }
  }

  const signature = typeof doc.signature === 'string' ? doc.signature.trim() : null;
  if (!signature) {
    return { ok: false, error: 'signature invalid', code: 'missing-signature', message: 'signature invalid: missing signature' };
  }

  const hasPayload = doc.payload !== undefined;
  const payloadCandidate = hasPayload ? doc.payload : undefined;
  const flatCandidate = !hasPayload ? {
    org: doc.org ?? doc.orgId,
    plan: doc.plan,
    ...(doc.entitlements !== undefined ? { entitlements: doc.entitlements } : {}),
    expiry: doc.expiry ?? doc.expires ?? doc.validUntil,
  } : undefined;

  const publicKey = opts.publicKeyBase64 ?? DEFAULT_LICENCE_ISSUER_PUBLIC_KEY;
  const verifier = opts.verifier ?? nodeVerifyEd25519;

  let verified = false;

  if (payloadCandidate !== undefined) {
    for (const msg of licenceSigningCandidates(payloadCandidate)) {
      if (verifier(publicKey, msg, signature)) {
        verified = true;
        break;
      }
    }
  } else if (flatCandidate && flatCandidate.plan) {
    for (const msg of licenceSigningCandidates(flatCandidate)) {
      if (verifier(publicKey, msg, signature)) {
        verified = true;
        break;
      }
    }
  }

  if (!verified) {
    return {
      ok: false,
      error: 'signature invalid',
      code: 'signature-invalid',
      message: 'signature invalid',
    };
  }

  // Extract fields
  const p = (payloadCandidate && typeof payloadCandidate === 'object' ? payloadCandidate : flatCandidate) as Record<string, unknown>;
  const org = typeof p.org === 'string' ? p.org : typeof p.orgId === 'string' ? p.orgId : 'self-hosted';
  const plan = p.plan as PlanId;
  const expiryRaw = (p.expiry ?? p.expires ?? p.validUntil) as number | string;

  if (!isPlanId(plan)) {
    return { ok: false, error: 'malformed', code: 'bad-plan', message: `Unknown plan: ${String(plan)}` };
  }

  const expiryMs = parseLicenceExpiry(expiryRaw);
  if (expiryMs === null) {
    return { ok: false, error: 'malformed', code: 'bad-expiry', message: 'Licence expiry is invalid' };
  }

  const now = opts.now ?? Date.now();
  if (expiryMs <= now) {
    return {
      ok: false,
      error: 'expired',
      code: 'expired',
      message: 'expired',
    };
  }

  return {
    ok: true,
    licence: {
      org,
      plan,
      overrides: normalizeOverrides(p.entitlements),
      expiry: expiryMs,
      expiryRaw,
      signature,
    },
  };
}

/** Async wrapper for verifyLicenceSync, supporting async verifiers if provided. */
export async function verifyLicence(
  input: unknown,
  opts: {
    publicKeyBase64?: string;
    now?: number;
    verifier?: Ed25519Verify;
  } = {},
): Promise<LicenceVerificationResult> {
  // If custom verifier provided and returns Promise, handle async
  if (opts.verifier) {
    const syncVer = (pub: string, msg: string, sig: string): boolean => {
      const res = opts.verifier!(pub, msg, sig);
      if (typeof res === 'boolean') return res;
      throw new Error('Async verifier passed to verifyLicenceSync');
    };
    try {
      return verifyLicenceSync(input, { ...opts, verifier: syncVer });
    } catch {
      // Handle async fallback
    }
  }
  return verifyLicenceSync(input, opts as Parameters<typeof verifyLicenceSync>[1]);
}
